import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';

export interface Site {
  id: string;
  code: string;
  name: string;
  buildingCode: string | null;
  subnets: string[];
  timezone: string | null;
}

interface SitesResponse {
  sites: Site[];
}

interface CreateSiteData {
  code: string;
  name: string;
  buildingCode?: string | null;
  subnets?: string[];
  timezone?: string;
}

interface UpdateSiteData {
  code?: string;
  name?: string;
  buildingCode?: string | null;
  subnets?: string[];
  timezone?: string | null;
}

/**
 * Hook to fetch all sites
 */
export function useSites() {
  return useQuery({
    queryKey: queryKeys.sites.list(),
    queryFn: async () => {
      const response = await api.get<SitesResponse>(endpoints.sites.list);
      return response.sites;
    },
  });
}

/**
 * Hook to create a new site
 */
export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSiteData) => {
      return api.post<{ success: boolean; site: Site }>(endpoints.sites.list, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}

/**
 * Hook to update a site
 */
export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSiteData }) => {
      return api.put<{ success: boolean; site: Site }>(`${endpoints.sites.list}/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}

/**
 * Hook to delete a site
 */
export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return api.delete<{ success: boolean; message: string }>(`${endpoints.sites.list}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}

/**
 * Hook to update a device's site assignment
 */
export function useUpdateDeviceSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, siteId }: { deviceId: string; siteId: string | null }) => {
      return api.patch(endpoints.devices.updateSite(deviceId), { siteId });
    },
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.detail(deviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}
